import Requests from "@/services/Requests";

const state = {
  discounts: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  allDiscounts: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  createStatus: "unitiliazed", // unitiliazed, loading, success, error
  updateStatus: "unitiliazed", // unitiliazed, loading, success, error
  deleteStatus: "unitiliazed", // unitiliazed, loading, success, error
  discountById: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
};
const getters = {
  /**
   * Returns a subset of discounts based on the given page number.
   *
   * @param {Object} state - The Vuex state object.
   * @param {number} [page=0] - The page number to filter discounts by. Defaults to 0.
   * @return {Array} An array of discounts filtered by the given page number. If page is 0, returns all discounts.
   */
  getDiscounts:
    (state) =>
    (page = 0) =>
      page === 0
        ? state.discounts.data
        : state.discounts.data
            .filter(
              (value, index, self) =>
                index === self.findIndex((t) => t.id === value.id)
            )
            .filter((d) => d.page === page),
  getAllDiscounts: (state) => state.allDiscounts.data,
  getDiscountStatus: (state) => state.discounts.status,
  getDiscountCount: (state) => state.discounts.count,
  getDiscountPage: (state) => state.discounts.page,
  getDiscountsLoading: (state) => state.discounts.status === "loading",
  getDiscountById: (state) => (id) =>
    state.discountById.data.find((d) => d.id === id) || {},
};
const mutations = {
  /**
   * Updates the status of the discount in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the discount.
   */
  SET_DISCOUNT_STATUS(state, payload) {
    console.log(state.discounts, "status: ", payload);
    state.discounts.status = payload;
  },
  /**
   * Sets the discounts and count in the state based on the payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the items and count.
   * @param {Array} payload.items - The array of discounts.
   * @param {number} payload.count - The total count of discounts.
   */
  SET_DISCOUNTS(state, payload) {
    state.discounts.data = payload.items;
    state.discounts.count = payload.count;
  },
  /**
   * Sets the discounts and count in the state based on the payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the items and count.
   * @param {Array} payload.items - The array of discounts.
   * @param {number} payload.count - The total count of discounts.
   */
  SET_ALL_DISCOUNTS(state, payload) {
    console.log("SET_ALL_DISCOUNTS", payload);
    state.allDiscounts.data = payload.items;
    state.allDiscounts.count = payload.count;
  },
  /**
   * Updates the status of the update discount in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the discount.
   */
  SET_UPDATE_DISCOUNT_STATUS(state, payload) {
    state.updateStatus = payload;
  },
  /**
   * Updates an discount object in the state with new information.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The updated discount object.
   * @return {void}
   */
  SET_UPDATE_DISCOUNT(state, payload) {
    const index = state.discounts.data.findIndex((d) => d.id === payload.id);
    const discount = state.discounts.data[index];
    state.discounts.data[index] = { ...discount, ...payload };
  },
  SET_CREATE_DISCOUNT_STATUS(state, payload) {
    state.createStatus = payload;
  },
  /**
   * Adds a new discount to the list of discounts in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The discount object to be added.
   * @return {void}
   */
  SET_CREATE_DISCOUNT(state, payload) {
    state.discounts.data.push(payload);
  },
  /**
   * Updates the status of the delete discount in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the discount.
   */
  SET_DELETE_DISCOUNT_STATUS(state, payload) {
    state.deleteStatus = payload;
  },
  /**
   * Removes an discount from the state based on the provided payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the discount ID to be deleted.
   * @param {number} payload.id - The ID of the discount to be deleted.
   * @return {void}
   */
  SET_DELETE_DISCOUNT(state, payload) {
    state.discounts.data = state.discounts.data.filter(
      (d) => d.id !== payload.id
    );
  },
  SET_DISCOUNT_BY_ID_STATUS(state, payload) {
    state.discountById.status = payload;
  },
  SET_DISCOUNT_BY_ID(state, payload) {
    state.discountById.data.push(payload);
  },
};

const actions = {
  /**
   * Retrieves a list of discounts based on the provided payload.
   *
   * @param {Object} commit - The Vuex commit function.
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the query parameters.
   * @param {Object} payload.query - The query parameters.
   * @param {string} payload.query.paymentMethodId - The ID of the payment method to filter by.
   * @return {Promise<void>}
   */
  async getDiscounts({ commit, state }, payload) {
    const status = state.discounts.status;
    commit("SET_DISCOUNT_STATUS", "loading");
    const response = await Requests.Discount.GetAll(payload);
    if (response.message === "OK") {
      commit("SET_DISCOUNTS", {
        items: response.data.map((item) => ({
          ...item,
          page: payload.query.page || 1,
        })),
        count: response.data.length,
      });
      if (status === "unitiliazed")
        commit("SET_ALL_DISCOUNTS", {
          items: response.data.map((item) => ({
            ...item,
            page: payload.query.page || 1,
          })),
          count: response.data.length,
        });
    }
    commit("SET_DISCOUNT_STATUS", "success");
    return response.message;
  },
  /**
   * Creates an discount by making a request to the server.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the discount data.
   * @param {string} payload.paymentMethodId - The ID of the payment method.
   * @param {string} payload.discountRate - The discount rate.
   * @return {Promise<void>} - A promise that resolves when the creation is complete.
   */
  async createDiscount({ state, commit }, payload) {
    state.createStatus = "loading";
    try {
      const response = await Requests.Discount.Create(payload);
      state.createStatus = "success";
      if (response.message === "OK")
        commit("SET_CREATE_DISCOUNT", {
          id: response.data,
          paymentMethodId: payload.paymentMethodId,
          discountRate: payload.discountRate,
          isActive: true,
          page: 1,
        });
      else state.createStatus = "error";
    } catch {
      state.createStatus = "error";
    }
  },
  /**
   * Updates an discount by making a request to the server.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the discount data to update.
   * @param {string} payload.paymentMethodId - The ID of the payment method.
   * @param {string} payload.discountRate - The discount rate.
   * @param {boolean} payload.isActive - Whether the discount is active or not.
   * @return {Promise<void>} - A promise that resolves when the update is complete.
   */
  async updateDiscount({ state, commit }, payload) {
    state.updateStatus = "loading";
    try {
      const response = await Requests.Discount.Update(payload, {
        params: { id: payload.id },
      });
      if (response.message === "OK") {
        commit("SET_UPDATE_DISCOUNT", payload);
        state.updateStatus = "success";
      } else {
        state.updateStatus = "error";
      }
    } catch {
      state.updateStatus = "error";
    }
  },
  /**
   * Removes an discount from the state and updates the delete status.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the discount data.
   * @param {number} payload.id - The ID of the discount to be removed.
   * @returns {Promise<void>} A promise that resolves when the discount is successfully removed.
   */
  removeDiscount({ state }, { id }) {
    state.deleteStatus = "loading";

    return Requests.Discount.Delete({ params: { id } })
      .then((response) => {
        state.deleteStatus = response.message === "OK" ? "success" : "error";
        return response;
      })
      .catch(() => {
        state.deleteStatus = "error";
        return { message: "ERROR" };
      });
  },
  /**
   * Retrieves an discount by their ID and updates the discount by ID status in the Vuex store.
   *
   * @param {Object} commit - The Vuex commit function.
   * @param {Object} id - The ID of the discount to retrieve.
   * @return {Promise} A promise that resolves when the discount is successfully retrieved and the status is updated.
   */
  getDiscountById({ commit, state }, { id }) {
    // check if discount already exists in the state;
    if (state.discountById.data.find((discount) => discount.id === id)) return;
    commit("SET_DISCOUNT_BY_ID_STATUS", "loading");
    return Requests.Discount.GetById({ params: { id } })
      .then((response) => {
        commit("SET_DISCOUNT_BY_ID", response.data);
        commit("SET_DISCOUNT_BY_ID_STATUS", "success");
      })
      .catch(() => {
        commit("SET_DISCOUNT_BY_ID_STATUS", "error");
      });
  },
};

export default { state, getters, mutations, actions };
